import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { useCurrentUser } from 'vuefire';

type CustomClaims = Record<string, unknown>;

export interface AccountAccess {
  isAdmin: Ref<boolean>;
  partnerIds: Ref<string[]>;
  isPartnerOnly: ComputedRef<boolean>;
}

export function partnerIdsFromClaims(claims: CustomClaims): string[] {
  const partnerIds = Array.isArray(claims.partnerIds)
    ? claims.partnerIds.filter((partnerId): partnerId is string => typeof partnerId === 'string')
    : typeof claims.partnerId === 'string' ? [claims.partnerId] : [];
  return [...new Set(partnerIds)];
}

export function isPartnerOnlyClaims(claims: CustomClaims): boolean {
  return claims.admin !== true && partnerIdsFromClaims(claims).length > 0;
}

export function defaultSignedInRouteName(claims: CustomClaims): 'pets' | 'partner-qr-sets' {
  return isPartnerOnlyClaims(claims) ? 'partner-qr-sets' : 'pets';
}

export function useAccountAccess(): AccountAccess {
  const user = useCurrentUser();
  const isAdmin = ref(false);
  const partnerIds = ref<string[]>([]);

  watch(
    user,
    async (value) => {
      if (!value) {
        isAdmin.value = false;
        partnerIds.value = [];
        return;
      }

      try {
        // Force a refresh so newly granted staff access appears without a new sign-in.
        const token = await value.getIdTokenResult(true);
        isAdmin.value = token.claims.admin === true;
        partnerIds.value = partnerIdsFromClaims(token.claims);
      } catch {
        isAdmin.value = false;
        partnerIds.value = [];
      }
    },
    { immediate: true },
  );

  return {
    isAdmin,
    partnerIds,
    isPartnerOnly: computed(() => !isAdmin.value && partnerIds.value.length > 0),
  };
}
